import { normalize } from "normalizr";
import { camelizeKeys } from "humps";
import api from "api/reportsApi";
import * as actionTypes from "../actionTypes/reports";
import * as eventTypes from "../eventTypes/reports";
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from "./httpActions";
import { showError } from "actions/actionCreators/snackbarActions";
import schemas from "utils/schemas";
import { env } from "utils/env";

export const loadReportsKey = (filters, pageSize) => {
  return JSON.stringify({ ...filters, pageSize });
};

export function loadReports(filters, page, pageSize) {
  return async (dispatch, getState) => {
    const key = loadReportsKey(filters, pageSize);
    const reports = getState().reports.list[key];
    let shouldCallApi = !(reports && reports[page]);
    if (!shouldCallApi) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_REPORTS_STARTED, key, page, pageSize });

    try {
      const response = await dispatch(sendHttpRequest(api.getReports(filters, page, pageSize)));
      const camelizedJson = camelizeKeys(response.values);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.reports));

      dispatch({
        entitiesCategory: "reports",
        type: actionTypes.LOAD_REPORTS_SUCCEEDED,
        key,
        page,
        totalCount: response.totalCount,
        totalPages: response.totalPages,
        ...payload
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_REPORTS_FAILED, key, page, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadReport(reportId, reload = false) {
  return async (dispatch, getState) => {
    const report = getState().reports.items[reportId];
    let shouldCallApi = reload || !report || (!report.loaded && !report.loading);
    if (!shouldCallApi) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_REPORT_STARTED, key: reportId });

    try {
      const response = await dispatch(sendHttpRequest(api.getReport(reportId)));

      const camelizedJson = camelizeKeys(response);
      const payload = Object.assign({}, normalize(camelizedJson, schemas.report));

      dispatch({ entitiesCategory: "reports", type: actionTypes.LOAD_REPORT_SUCCEEDED, key: reportId, ...payload });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_REPORT_FAILED, key: reportId, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function generateReport(report) {
  return async dispatch => {
    dispatch({ type: actionTypes.GENERATE_REPORT_STARTED, key: report.reportId });

    try {
    
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.generateReport({ ...report, filters: report.filters }),
          null,
          [eventTypes.REPORT_GENERATED],
          false,
          env.REACT_APP_REPORTS_HTTP_TIMEOUT
        )
      );
      if (event.ok) {
        const url = api.buildDownloadUrl(event.eventBody.filePath, event.eventBody.fileName);
        dispatch({ type: actionTypes.GENERATE_REPORT_SUCCEEDED, key: report.reportId, url });
        window.location = url;
      } else {
        dispatch({ type: actionTypes.GENERATE_REPORT_FAILED, key: report.reportId });
      }
    } catch (error) {
      dispatch({ type: actionTypes.GENERATE_REPORT_FAILED, key: report.reportId });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadReportLookupDataSource(reportId, reportFilterId) {
  return async dispatch => {
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.getReportLookupDataSource(reportId, reportFilterId),
          null,
          [eventTypes.REPORT_LOOKUP_DS_FOUND],
          false
        )
      );

      if (event.ok) {
        const options = event.eventBody.options.map((item, index) => {

          return Object.assign({}, item, {
            idx: index + 1
          });
        });
        return options;
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadBrowser(reportFilterId, reportId, browserId, reload = false) {
  return async (dispatch, getState) => {
    const browser = getState().reports.browserItems[browserId];
    let shouldCallApi = reload || !browser || (!browser.loaded && !browser.loading);
    if (!shouldCallApi) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_BROWSER_STARTED, key: browserId });
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.getReportBrowser(reportFilterId, reportId),
          null,
          [eventTypes.REPORT_BROWSER_LOADED],
          false
        )
      );
      if (event.ok) {
        const camelizedJson = camelizeKeys(event.eventBody);
        const payload = Object.assign({}, normalize(camelizedJson, schemas.browser));
        dispatch({ entitiesCategory: "reports", type: actionTypes.LOAD_BROWSER_SUCCEEDED, key: browserId, ...payload });
      } else {
        dispatch({ type: actionTypes.LOAD_BROWSER_FAILED, key: browserId });
      }
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_BROWSER_FAILED, payload: { error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadBrowserData(
  filters,
  page,
  pageSize,
  browserId,
  reportFilterId,
  reportId,
  order,
  orderBy,
  orderByFieldName,
  reload = false
) {
  return async (dispatch, getState) => {
    const key = loadBrowserDataKey(filters, pageSize, order, orderBy);
    let browserData = null;
    if (getState().reports.browserDataList[browserId]) {
      browserData = getState().reports.browserDataList[browserId][key];
    }

    let shouldCallApi = reload || !(browserData && browserData[page]);
    if (!shouldCallApi) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_BROWSERDATA_STARTED, key, page, pageSize, browserId });
    try {
      const filtersUpdated = filters.map(filter => {
        if (filter.value === Object(filter.value)) {
          return { ...filter, value: filter.value ? filter.value.value : null };
        }
        return filter;
      });

      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.getReportBrowserData(
            filtersUpdated,
            page,
            pageSize,
            reportFilterId,
            reportId,
            order,
            orderBy,
            orderByFieldName
          ),
          null,
          [eventTypes.REPORT_BROWSERDATA_LOADED],
          false
        )
      );
      if (event.ok) {
        const response = event.eventBody;
        const camelizedJson = camelizeKeys(response.data);
        const payload = Object.assign({}, normalize(camelizedJson, schemas.getBrowserData(response.pkFieldName)));
        dispatch({
          browserId,
          entitiesCategory: "browserData",
          type: actionTypes.LOAD_BROWSERDATA_SUCCEEDED,
          key,
          page,
          totalCount: response.totalCount,
          totalPages: response.totalPages,
          ...payload
        });
      } else {
        dispatch({ type: actionTypes.LOAD_BROWSERDATA_FAILED, key, page, pageSize, browserId });
      }
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_BROWSERDATA_FAILED, key, page, pageSize, browserId, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function cleanBrowser(browserId) {
  return { type: actionTypes.CLEAN_BROWSER, browserId };
}

export const loadBrowserDataKey = (filters, pageSize, order, orderBy) => {
  return JSON.stringify({ ...filters, pageSize, order, orderBy });
};

export function loadBrowserLookupDataSource(browserId, filterName) {
  return async dispatch => {
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.getBrowserLookupDataSource(browserId, filterName),
          null,
          [eventTypes.BROWSER_LOOKUP_DS_FOUND],
          false
        )
      );

      if (event.ok) {
        const options = event.eventBody.options.map((o, idx) => {
          o["idx"] = idx + 1;
          return o;
        });
        return options;
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}
